import React, { useRef, useEffect, forwardRef } from 'react';

export interface CreateTelegramUser {
  id: number;
  first_name: string;
  username: string;
  photo_url: string;
  auth_date: number;
  hash: string;
}

interface Props {
  botName: string;
  usePic?: boolean;
  className?: string;
  cornerRadius?: number;
  requestAccess?: boolean;
  dataAuthUrl?: string;
  dataOnauth?: (user: CreateTelegramUser) => void;
  buttonSize?: 'large' | 'medium' | 'small';
  wrapperProps?: React.HTMLProps<HTMLDivElement>;
}

declare global {
  interface Window {
    TelegramLoginWidget: {
      dataOnauth: (user: CreateTelegramUser) => void;
    };
  }
}

export const TelegramLoginButton = forwardRef<HTMLDivElement, Props>(
  (
    {
      wrapperProps,
      dataAuthUrl,
      usePic = false,
      botName,
      className,
      buttonSize = 'large',
      dataOnauth,
      cornerRadius,
      requestAccess = true,
    },
    ref
  ) => {
    const localRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (localRef.current === null) return;

      if (
        typeof dataOnauth === 'undefined' &&
        typeof dataAuthUrl === 'undefined'
      ) {
        throw new Error(
          'One of this props should be defined: dataAuthUrl (redirect URL), dataOnauth (callback fn) should be defined.'
        );
      }

      if (typeof dataOnauth === 'function') {
        window.TelegramLoginWidget = {
          dataOnauth: (user: CreateTelegramUser) => dataOnauth(user),
        };
      }

      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', botName);
      script.setAttribute('data-size', buttonSize);

      if (cornerRadius !== undefined) {
        script.setAttribute('data-radius', cornerRadius.toString());
      }

      if (requestAccess) {
        script.setAttribute('data-request-access', 'write');
      }

      script.setAttribute('data-userpic', usePic.toString());

      if (typeof dataAuthUrl === 'string') {
        script.setAttribute('data-auth-url', dataAuthUrl);
      } else {
        script.setAttribute(
          'data-onauth',
          'TelegramLoginWidget.dataOnauth(user)'
        );
      }

      script.async = true;

      localRef.current.appendChild(script);
    }, [
      botName,
      buttonSize,
      cornerRadius,
      dataOnauth,
      requestAccess,
      usePic,
      dataAuthUrl,
    ]);

    const finalRef = (ref || localRef) as React.RefObject<HTMLDivElement>;

    return <div ref={finalRef} className={className} {...wrapperProps} />;
  }
);

TelegramLoginButton.displayName = 'TelegramLoginButton';

export default TelegramLoginButton; 